import { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton,
  Collapse,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import marketService from '../../services/MarketService';
import splinterlandsService from '../../services/SplinterlandsService';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DiamondTwoToneIcon from '@mui/icons-material/DiamondTwoTone';
import LeaderboardDialog from './LeaderboardDialog';
import Tooltip from '@mui/material/Tooltip';
import DiamondAnimation from '../animations/DiamondAnimation';

const CashbackDisplayCompact = ({ playerName }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [cashbackData, setCashbackData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false); // Default collapsed
  const [showRankHistory, setShowRankHistory] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [bonusPoolBalance, setBonusPoolBalance] = useState(0);
  const [poolLoading, setPoolLoading] = useState(false);
  
  // Use ref to prevent redundant API calls
  const playerNameRef = useRef(null);
  const isLoadingRef = useRef(false);
  
  // Animation states
  const [animationTrigger, setAnimationTrigger] = useState(0);
  const [lastProcessedPayoutId, setLastProcessedPayoutId] = useState(null);
  const cashbackRef = useRef(null);
  
  // Get target element (wallet in topbar)
  const getWalletElement = () => {
    // Versuche verschiedene Selektoren für das Wallet-Element in der Topbar
    const selectors = [
      '[data-wallet-target]', // Falls wir später ein data-attribute hinzufügen
      '.MuiAvatar-root', // Avatar Element
      '[aria-label*="account"]', // Account Button
      '.topbar-wallet' // Falls wir später eine Klasse hinzufügen
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    
    // Fallback: erstes Avatar Element
    return document.querySelector('.MuiAvatar-root');
  };
  
  // Trigger animation function
  const triggerCashbackAnimation = () => {
    setAnimationTrigger(prev => prev + 1);
  };

  // Load cashback data
  useEffect(() => {
    if (!playerName) return;
    
    // Prevent redundant calls if already loading for this player
    if (isLoadingRef.current && playerNameRef.current === playerName) {
      return;
    }
    
    // Skip if we already have data for this player
    if (playerNameRef.current === playerName && cashbackData) {
      return;
    }
    
    playerNameRef.current = playerName;
    isLoadingRef.current = true;

    const loadCashbackData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await marketService.getPlayerCashback(playerName);
        setCashbackData(data);
      } catch (err) {
        console.error('Error loading cashback:', err);
        setError(`Failed to load cashback data: ${err.message}`);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    const loadBonusPool = async () => {
      setPoolLoading(true);
      try {
        const balance = await splinterlandsService.getBonusPoolBalance();
        setBonusPoolBalance(balance);
      } catch (err) {
        console.error('Error loading bonus pool:', err);
      } finally {
        setPoolLoading(false);
      }
    };

    loadCashbackData();
    loadBonusPool();

    // Start payout polling for this player (less aggressive)
    marketService.startPayoutPolling(playerName, 10000); // Poll every 10 seconds instead of 5

    // Subscribe to market service updates
    const unsubscribe = marketService.subscribe((data) => {
      if (data.cashbackData) {
        setCashbackData(data.cashbackData);
      }
      
      // Check for payout events and trigger animation
      if (data.payoutEvent) {
        console.log('Payout event received:', data.payoutEvent);
        
        // Create a unique ID for this payout event
        const payoutId = data.payoutEvent.tx_hash || `${data.payoutEvent.timestamp}_${data.payoutEvent.amount}`;
        
        // Only trigger animation if:
        // 1. This is a real payout (amount > 0)
        // 2. We haven't processed this specific payout before
        if (data.payoutEvent.amount > 0 && payoutId !== lastProcessedPayoutId) {
          console.log('Triggering animation for payout:', payoutId);
          setLastProcessedPayoutId(payoutId);
          triggerCashbackAnimation();
        } else if (payoutId === lastProcessedPayoutId) {
          console.log('Skipping duplicate payout event:', payoutId);
        }
      }
    });

    return () => {
      unsubscribe();
      marketService.stopPayoutPolling(); // Stop polling when component unmounts
    };
  }, [playerName]);

  // Get rank color based on rank number
  const getRankColor = (rank) => {
    if (!rank) return colors.grey[400];
    if (rank <= 3) return colors.greenAccent[400]; // Top 3 - green
    if (rank <= 10) return colors.blueAccent[400]; // Top 10 - blue
    if (rank <= 50) return colors.redAccent[400]; // Top 50 - red
    return colors.grey[300]; // Others - grey
  };

  // Format cashback rate as percentage of 4% fee (65% of fee = 2.6% of transaction)
  const formatCashbackRate = (rate) => {
    if (!rate && rate !== 0) return '65%'; // Default base rate (2.6% / 4% = 65%)
    // Convert transaction percentage to fee percentage: rate / 4 * 100
    const feePercentage = (rate / 4 * 100).toFixed(0);
    return `${feePercentage}%`;
  };

  // Format number helper (same as original)
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0.000';
    return Number(value).toLocaleString('en-US', { 
      minimumFractionDigits: 3,
      maximumFractionDigits: 3 
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        p: 2, 
        backgroundColor: colors.primary[500],
        borderRadius: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 60
      }}>
        <CircularProgress size={24} sx={{ color: colors.blueAccent[400] }} />
      </Box>
    );
  }

  if (error && !cashbackData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" sx={{ backgroundColor: colors.redAccent[800] }}>
          Unable to load cashback data
        </Alert>
      </Box>
    );
  }

  if (!cashbackData) {
    return (
      <Box sx={{ 
        p: 2, 
        backgroundColor: colors.primary[500],
        borderRadius: 2,
        textAlign: 'center'
      }}>
        <Typography variant="body2" color={colors.grey[400]}>
          No cashback data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={cashbackRef}
      data-cashback-target="true"
      sx={{ 
        backgroundColor: colors.primary[500],
        borderRadius: 2,
        overflow: 'hidden',
        border: `2px solid #FFD700`, // Gelber Rand
        mb: 2,
        mr: 2, // Rechter Abstand
        boxShadow: '0 0 8px rgba(255, 215, 0, 0.3)', // Subtiler gelber Glow-Effekt
        position: 'relative'
      }}
    >
      {/* Compact Header - Always Visible */}
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        p={2}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: colors.primary[400]
          }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <TrendingUpIcon sx={{ 
            color: getRankColor(cashbackData.monthly_rank), 
            fontSize: 24 
          }} />
          
          {/* Rank Display - Clickable */}
          <Tooltip title="Click to view leaderboard" arrow>
            <Chip
              label={`#${cashbackData.monthly_rank || '∞'}`}
              sx={{
                backgroundColor: getRankColor(cashbackData.monthly_rank) + '20',
                color: getRankColor(cashbackData.monthly_rank),
                border: `1px solid ${getRankColor(cashbackData.monthly_rank)}`,
                fontWeight: 'bold',
                fontSize: '0.875rem',
                height: 28,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: getRankColor(cashbackData.monthly_rank) + '40',
                  transform: 'scale(1.05)'
                }
              }}
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent collapse toggle
                setShowLeaderboard(true);
              }}
            />
          </Tooltip>
          
          {/* Rate Display - Base Rate Only */}
          <Typography variant="body2" color={colors.grey[100]} fontWeight="600">
            {formatCashbackRate(cashbackData.base_rate)}
            {cashbackData.pool_percentage && cashbackData.pool_percentage > 0 && (
              <Typography component="span" variant="caption" color={colors.blueAccent[400]} sx={{ ml: 0.5 }}>
                +{cashbackData.pool_percentage}%
              </Typography>
            )}
          </Typography>
        </Box>
        
        {/* Expand/Collapse Icon */}
        <IconButton size="small" sx={{ color: colors.grey[300] }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expandable Details */}
      <Collapse in={expanded}>
        <Box sx={{ 
          p: 2, 
          pt: 0,
          background: `linear-gradient(135deg, ${colors.primary[800]}20 0%, ${colors.primary[900]}40 100%)`
        }}>
          {/* Main Stats Grid */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
            {/* Current Rate Card */}
            <Box sx={{
              backgroundColor: colors.greenAccent[500] + '15',
              border: `1px solid ${colors.greenAccent[500]}40`,
              borderRadius: 2,
              p: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h4" color={colors.greenAccent[400]} fontWeight="bold">
                {formatCashbackRate(cashbackData.base_rate)}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Base Rate
              </Typography>
              {cashbackData.pool_percentage && cashbackData.pool_percentage > 0 && (
                <Typography variant="caption" color={colors.grey[400]}>
                  + {cashbackData.pool_percentage}% of monthly pool
                </Typography>
              )}
            </Box>

            {/* Pending Cashback Card */}
            <Box sx={{
              backgroundColor: colors.blueAccent[500] + '15',
              border: `1px solid ${colors.blueAccent[500]}40`,
              borderRadius: 2,
              p: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h4" color={colors.blueAccent[400]} fontWeight="bold">
                {formatNumber(cashbackData.pending_cashback)}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Pending Cashback
              </Typography>
              <Typography variant="caption" color={colors.grey[400]}>
                DEC
              </Typography>
            </Box>
          </Box>

          {/* Earnings Summary */}
          <Box sx={{
            backgroundColor: colors.primary[600],
            borderRadius: 1,
            p: 2,
            mb: 2
          }}>
            {/* Total Cashback */}
            <Box>
              <Typography variant="subtitle1" color={colors.grey[100]} fontWeight="600" mb={1}>
                💰 Total Cashback
              </Typography>
              <Typography variant="h5" color={colors.greenAccent[400]} fontWeight="bold">
                {formatNumber(cashbackData.total_cashback_earned)} DEC
              </Typography>
            </Box>
          </Box>

          {/* Monthly Activity */}
          <Box>
            <Typography variant="subtitle2" color={colors.grey[300]} mb={1}>
              📊 This Month
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color={colors.grey[400]}>
                  Monthly Bonus Pool:
                </Typography>
                {poolLoading ? (
                  <CircularProgress size={16} sx={{ color: colors.blueAccent[400] }} />
                ) : (
                  <Typography variant="body2" color={colors.greenAccent[400]} fontWeight="bold">
                    {formatNumber(bonusPoolBalance)} DEC
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* Leaderboard Dialog */}
      <LeaderboardDialog
        open={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
        playerName={playerName}
      />

      {/* Development Test Button - Only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ p: 1, textAlign: 'center', borderTop: `1px solid ${colors.grey[700]}` }}>
          <Button
            variant="outlined"
            size="small"
            onClick={triggerCashbackAnimation}
            sx={{ 
              fontSize: '0.75rem',
              py: 0.5,
              px: 2,
              color: '#6c3eb6',
              borderColor: '#6c3eb6',
              '&:hover': {
                backgroundColor: '#6c3eb6',
                color: 'white'
              }
            }}
          >
            <DiamondTwoToneIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Test Animation
          </Button>
        </Box>
      )}

      {/* Diamond Animation Component */}
      <DiamondAnimation 
        trigger={animationTrigger}
        sourceElement={cashbackRef.current}
        targetElement={getWalletElement()}
        amount={cashbackData?.pending_cashback || "1.000"}
        onAnimationComplete={() => console.log('Animation completed!')}
      />
    </Box>
  );
};

export default CashbackDisplayCompact;
